import { OnChange } from '@/features/form/types'
import {
    FormKeys,
    maxProductQuantity,
} from '@/features/pos/components/ProductConfigurationModal/form/constants'
import { schema } from '@/features/pos/components/ProductConfigurationModal/form/schema'
import {
    Form,
    FormAddonGroup,
} from '@/features/pos/components/ProductConfigurationModal/form/types'
import { getDefaultValues } from '@/features/pos/components/ProductConfigurationModal/form/utils/getDefaultValues'
import {
    AddonGroup,
    ProductVariant,
} from '@/features/pos/components/ProductConfigurationModal/types'
import { toFormAddonGroup } from '@/features/pos/components/ProductConfigurationModal/utils/toFormAddonGroup'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useRef } from 'react'
import {
    UseFormReturn,
    useController,
    useFormContext,
    useForm as useRHFForm,
} from 'react-hook-form'

export const useForm = (
    productVariant?: ProductVariant,
    defaultQuantity?: number
): UseFormReturn<Form> => {
    const initialized = useRef(!!productVariant)
    const methods = useRHFForm<Form>({
        defaultValues: productVariant && getDefaultValues(productVariant, defaultQuantity),
        resolver: zodResolver(schema),
        mode: 'onChange',
    })

    useEffect(() => {
        if (productVariant && !initialized.current) {
            methods.reset(getDefaultValues(productVariant, defaultQuantity))
            initialized.current = true
        }
    }, [productVariant, defaultQuantity, methods])

    return methods
}

export const useFormIsDefaultValuesInitialized = (): boolean => {
    const { formState } = useFormContext()

    return !!formState.defaultValues
}

export const useFormQuantity = () => {
    const { getValues, setValue } = useFormContext<Form>()
    const {
        field: { value: quantity },
        formState: { isSubmitting },
    } = useController<Form, FormKeys.Quantity>({ name: FormKeys.Quantity })

    const changeQuantity = (change: number) => {
        if (isSubmitting) return

        const nextQuantity = getValues(FormKeys.Quantity) + change

        if (nextQuantity < 1 || nextQuantity > maxProductQuantity) return

        setValue(FormKeys.Quantity, nextQuantity, { shouldDirty: true, shouldValidate: true })
    }

    return {
        quantity,
        disabled: isSubmitting,
        decrease: () => changeQuantity(-1),
        increase: () => changeQuantity(1),
    }
}

export const useFormProductVariantId = (): [string, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<Form, FormKeys.ProductVariantId>({ name: FormKeys.ProductVariantId })

    return [value, onChange]
}

export const useFormAddonGroup = (addonGroup: AddonGroup): [FormAddonGroup, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<Form, `${FormKeys.AddonGroups}.${string}`>({
        name: `${FormKeys.AddonGroups}.${addonGroup.id}`,
        defaultValue: toFormAddonGroup(addonGroup, {}),
    })

    return [value, onChange]
}

export const useFormIsIngredientRemoved = (
    removedIngredientId: string
): [boolean | undefined, OnChange] => {
    const {
        field: { value, onChange },
    } = useController<Form, `${FormKeys.RemovedIngredients}.${string}`>({
        name: `${FormKeys.RemovedIngredients}.${removedIngredientId}`,
    })

    return [value, onChange]
}

export const useFormUnregisterField = (fieldName: string): void => {
    const { unregister, trigger } = useFormContext()

    useEffect(() => {
        return () => {
            unregister(fieldName)

            // schedule the macrotask to ensure revalidation runs after the form state updates from unregister
            setTimeout(() => trigger(fieldName))
        }
    }, [fieldName, unregister, trigger])
}
